import {
  Input,
  OnChanges,
  ViewChild,
  Component,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import {
  FormsService,
  SensikaForms,
} from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { RoleApiService } from 'src/app/api/services/role-api.service';

import { GeneralDynamicFormComponent } from '../../general-dynamic-form/general-dynamic-form.component';

import { User } from 'src/app/core/models/core.models';
import { FormProperty } from 'src/app/core/models/form.models';

import { AppState } from 'src/app/core/state/app.state';

import { BehaviorSubject, catchError, map, of, tap } from 'rxjs';
import { SearchInputComponent } from '../../search-input/search-input.component';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AsyncPipe } from '@angular/common';
import { HighlightPipe } from '../../../pipes/highlight.pipe';

@Component({
  selector: 'app-user-permissions',
  templateUrl: './user-permissions.component.html',
  styleUrls: ['./user-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SearchInputComponent,
    FormsModule,
    FaIconComponent,
    AsyncPipe,
    HighlightPipe,
  ],
})
export class UserPermissionsComponent
  extends GeneralDynamicFormComponent
  implements OnChanges
{
  @Input() user: User;
  @Input() searchString$: BehaviorSubject<string>; // From parent for cross-tab search
  @ViewChild('permissionSearch') permissionSearch: ElementRef;

  localSearchString: string = '';
  globalSearchString: string = '';
  aggregatedFormId: string = SensikaForms.USER_EDITOR;

  userModules$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  get activeSearchString(): string {
    return this.globalSearchString || this.localSearchString;
  }

  get isLocalSearchDisabled(): boolean {
    return this.globalSearchString?.length > 0;
  }

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    protected roleApi: RoleApiService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.populateForm();

    // * Subscribe to parent global search for cross-tab search integration
    if (this.searchString$) {
      this.searchString$
        .pipe(
          tap((val) => {
            this.globalSearchString = val;

            // When global search becomes active, clear local search
            if (val?.length > 0) {
              this.localSearchString = '';
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  ngOnChanges(): void {
    this.fetchData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setPermissionsHeight();
    }, 1000);
  }

  fetchData(): void {
    if (this.user && this.user.feature_components) {
      this.roleApi
        .getModuleComponents()
        .pipe(
          map((data) =>
            this.processPermissions(data, this.user.feature_components),
          ),
          tap((processedModules) => this.userModules$.next(processedModules)),
          catchError((err) => {
            this.logger.error('Failed to fetch module components', err);
            return of([]);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      this.logger.warn('User permissions data is not available.');
    }
  }

  processPermissions(userPermissions: any[], currentUserPermissions: any) {
    return userPermissions.map((module) => ({
      ...module,
      root_component: {
        ...module.root_component,
        selected: currentUserPermissions[module.root_component.id],
      },
      components: module.components.map((component) => ({
        ...component,
        selected: currentUserPermissions[component.id],
      })),
    }));
  }

  populateForm(): void {
    this.formService.populateFormValues(this.formGroup, this.user);
  }

  public formProperties: FormProperty[] = [
    {
      propertyName: 'permissions',
      propertyLabel: 'User Permissions',
      controlType: 'textarea',
    },
  ];

  handlePermissionSearch(searchString: string): void {
    // Legacy method - can be removed if not used elsewhere
    this.localSearchString = searchString;
  }

  shouldHideModule(roleModule: any, searchString: string): boolean {
    if (!roleModule || !this.activeSearchString) {
      return false;
    }
    const search = this.activeSearchString.toLowerCase();
    const nameDoesNotIncludeSearchString = !roleModule.name
      .toLowerCase()
      .includes(search);
    const componentsDoNotIncludeSearchString = !roleModule.components.some(
      (component) => component.name.toLowerCase().includes(search),
    );

    return nameDoesNotIncludeSearchString && componentsDoNotIncludeSearchString;
  }

  shouldHideComponent(
    roleModule: any,
    component: any,
    searchString: string,
  ): boolean {
    if (!roleModule || !this.activeSearchString) {
      return false;
    }
    const search = this.activeSearchString.toLowerCase();
    const componentsDoNotIncludeSearchString = !component.name
      .toLowerCase()
      .includes(search);

    return componentsDoNotIncludeSearchString;
  }

  /**
   * Returns count of permissions matching GLOBAL search string only
   * Used by parent form editor for cross-tab search functionality
   * Local search does NOT report to parent (tab-specific only)
   */
  getSearchStringMatchesCount(): number {
    // Only report matches for GLOBAL search, not local
    if (!this.globalSearchString || !this.globalSearchString.trim()) {
      return 0;
    }

    const currentModules = this.userModules$.getValue();
    if (!currentModules || currentModules.length === 0) {
      return 0;
    }

    const search = this.globalSearchString.toLowerCase().trim();
    let matchCount = 0;

    currentModules.forEach((module) => {
      // * check root component/module name
      if (module.name?.toLowerCase().includes(search)) {
        matchCount++;
      }

      // * check child components
      module.components?.forEach((component) => {
        if (component.name?.toLowerCase().includes(search)) {
          matchCount++;
        }
      });
    });

    return matchCount;
  }

  setPermissionsHeight(): void {
    // TODO: setting the height this way is an anti-pattern but all issues around "dynamic forms"
    // TODO: are causing this dirty hack that is necessary and probably the most optimal solution for now
    // TODO: remove once all "dynamic forms" are refactored and deleted!
    const container: HTMLElement = document.querySelector('.form-tab-content');
    const containerHeight: number = container?.clientHeight;
    const scrollableContainer: HTMLElement = document.querySelector(
      '.permissions-container',
    );

    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );

    const heightInRem = (containerHeight - 64) / rootFontSize;

    if (containerHeight && scrollableContainer) {
      scrollableContainer.style.height = `${heightInRem}rem`;
    }
  }
}
